import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Award, Gift, Undo2, Trophy } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

export default function HistoryTab({ classId, students, reload }) {
  const { t } = useI18n();
  const TYPES = { event: t("typePoints"), badge: t("typeBadge"), reward: t("typeReward"), medal: t("typeMedal") };
  const FILTERS = [["all", t("all")], ["event", t("typePoints")], ["badge", t("badges")], ["medal", t("medals")], ["reward", t("rewards")]];
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [studentId, setStudentId] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (type !== "all") params.type = type;
    if (studentId !== "all") params.student_id = studentId;
    setItems(await api.getHistory(classId, params));
  }, [classId, type, studentId]);
  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  const undo = async (h) => {
    if (!window.confirm(`${t("undo")} · ${h.action} (${h.student_name})?`)) return;
    await api.undoHistory(h.id);
    toast.success(t("undoDone"));
    load(); reload && reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([v, l]) => (
          <button key={v} data-testid={`history-filter-${v}`} onClick={() => setType(v)}
            className={`px-4 h-11 rounded-2xl text-sm font-bold border-2 ${type === v ? "border-primary bg-accent" : "border-border bg-muted"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="history-student-all" onClick={() => setStudentId("all")} className={`px-4 h-10 rounded-2xl text-sm font-bold border-2 ${studentId === "all" ? "border-primary bg-accent" : "border-border bg-muted"}`}>{t("allStudents")}</button>
        {students.map((s) => <button key={s.id} data-testid={`history-student-${s.id}`} onClick={() => setStudentId(s.id)} className={`px-3 h-10 rounded-2xl text-sm font-bold border-2 ${studentId === s.id ? "border-primary bg-accent" : "border-border bg-muted"}`}>{s.name}</button>)}
      </div>

      <div className="bg-card rounded-3xl border-2 border-border divide-y divide-border overflow-hidden">
        {items.length === 0 && <div className="text-center text-muted-foreground py-10">{t("noRecords")}</div>}
        {items.map((h) => (
          <div key={h.id} data-testid={`history-${h.id}`} className="flex items-center gap-3 p-3.5 hover:bg-muted transition-colors">
            <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center text-lg shrink-0">
              {h.type === "badge" ? <Award className="w-5 h-5 text-yellow-500" /> : h.type === "medal" ? <Trophy className="w-5 h-5 text-yellow-500" /> : h.type === "reward" ? <Gift className="w-5 h-5 text-fuchsia-500" /> : <span>{h.emoji}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{h.student_name} · <span className="text-muted-foreground font-normal">{h.action}</span></div>
              <div className="text-xs text-muted-foreground">{TYPES[h.type] || h.type} · {fmt(h.timestamp)}</div>
            </div>
            {!!h.points && <span className={`font-extrabold ${h.points >= 0 ? "text-emerald-500" : "text-red-500"}`}>{h.points >= 0 ? "+" : ""}{h.points}</span>}
            <Button data-testid={`undo-${h.id}`} variant="outline" size="sm" onClick={() => undo(h)} className="rounded-full gap-1 shrink-0 h-10"><Undo2 className="w-4 h-4" /> <span className="hidden sm:inline">{t("undo")}</span></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
